import React from 'react';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 'ALL'];
const DEFAULT_PAGE_SIZE = 100;
const STORAGE_KEY = 'sfx_page_size';

/**
 * 'ALL' is NEVER remembered (owner ruling 2026-09-26): every page load opens
 * at a number, and All lasts only until the user leaves or refreshes. A
 * remembered All drew all 7,000+ Dept Orders rows on every visit and every
 * 30 s poll. A stored 'ALL' from before the ruling reads as the default.
 */
function getStoredPageSize() {
  try {
    const num = parseInt(localStorage.getItem(STORAGE_KEY), 10);
    if (PAGE_SIZE_OPTIONS.includes(num)) return num;
  } catch (e) {
    // ignore
  }
  return DEFAULT_PAGE_SIZE;
}

function savePageSize(size) {
  try {
    // Choosing All clears the saved size, so the next load is the default.
    if (size === 'ALL') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(size));
  } catch (e) {
    // ignore
  }
}

/**
 * Pagination component for order list views.
 *
 * Props:
 *   totalItems - total number of filtered items
 *   currentPage - current page number (1-based)
 *   pageSize - current page size (number or 'ALL')
 *   onPageChange - callback(pageNumber)
 *   onPageSizeChange - callback(newPageSize)
 */
function Pagination({ totalItems, currentPage, pageSize, onPageChange, onPageSizeChange, extraControls }) {
  const isAll = pageSize === 'ALL';
  const totalPages = isAll ? 1 : Math.ceil(totalItems / pageSize) || 1;

  // Compute visible page range (show max 7 page buttons)
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = [];
    // Always show first page
    pages.push(1);

    let start = Math.max(2, currentPage - 2);
    let end = Math.min(totalPages - 1, currentPage + 2);

    // Adjust range if near edges
    if (currentPage <= 4) {
      end = Math.min(5, totalPages - 1);
    }
    if (currentPage >= totalPages - 3) {
      start = Math.max(2, totalPages - 4);
    }

    if (start > 2) pages.push('...');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push('...');

    // Always show last page
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  const startItem = isAll ? 1 : (currentPage - 1) * pageSize + 1;
  const endItem = isAll ? totalItems : Math.min(currentPage * pageSize, totalItems);

  const handlePageSizeChange = (newSize) => {
    savePageSize(newSize);
    onPageSizeChange(newSize);
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-3 px-1" data-testid="pagination">
      {/* Left: Items per page selector — hidden on mobile (mobile uses fixed page size) */}
      <div className="hidden md:flex items-center gap-1.5 sm:gap-2 text-sm text-gray-600">
        <span>Show</span>
        <div className="flex rounded-md border border-gray-300 overflow-hidden">
          {PAGE_SIZE_OPTIONS.map(option => (
            <button
              key={option}
              onClick={() => handlePageSizeChange(option)}
              data-testid={`page-size-${option}`}
              className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors border-r last:border-r-0 border-gray-300 ${
                pageSize === option
                  ? 'bg-blue-600 dark:bg-blue-500/70 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
        <span>per page</span>
        {extraControls && extraControls}
      </div>

      {/* Center: Showing X-Y of Z */}
      <div className="text-sm text-gray-500 dark:text-gray-400" data-testid="pagination-info">
        {totalItems === 0
          ? 'No items'
          : `Showing ${startItem}-${endItem} of ${totalItems}`}
      </div>

      {/* Right: Page navigation — full list on desktop, simplified prev/next on mobile */}
      {!isAll && totalPages > 1 && (
        <>
          {/* Desktop: full page-number list */}
          <div className="hidden md:flex items-center gap-1" data-testid="page-numbers">
            {/* Previous button */}
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className={`px-2 py-1.5 rounded text-sm font-medium transition-colors ${
                currentPage <= 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              aria-label="Previous page"
            >
              ‹
            </button>

            {/* Page numbers */}
            {getPageNumbers().map((page, idx) =>
              page === '...' ? (
                <span key={`ellipsis-${idx}`} className="px-2 py-1.5 text-sm text-gray-400">
                  …
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  data-testid={`page-${page}`}
                  className={`min-w-[32px] px-2 py-1.5 rounded text-sm font-medium transition-colors ${
                    currentPage === page
                      ? 'bg-blue-600 dark:bg-blue-500/70 text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  {page}
                </button>
              )
            )}

            {/* Next button */}
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className={`px-2 py-1.5 rounded text-sm font-medium transition-colors ${
                currentPage >= totalPages
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              aria-label="Next page"
            >
              ›
            </button>
          </div>

          {/* Mobile: prev | Page X of Y | next — large touch-friendly buttons */}
          <div className="md:hidden flex items-center justify-between w-full gap-2" data-testid="page-numbers-mobile">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className={`flex-1 min-h-[44px] px-3 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-1 ${
                currentPage <= 1
                  ? 'bg-gray-100 text-gray-300 dark:bg-gray-800 dark:text-gray-600 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500/70 dark:hover:bg-blue-500'
              }`}
              aria-label="Previous page"
            >
              <span aria-hidden="true">‹</span> Prev
            </button>
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap px-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className={`flex-1 min-h-[44px] px-3 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-1 ${
                currentPage >= totalPages
                  ? 'bg-gray-100 text-gray-300 dark:bg-gray-800 dark:text-gray-600 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500/70 dark:hover:bg-blue-500'
              }`}
              aria-label="Next page"
            >
              Next <span aria-hidden="true">›</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export { getStoredPageSize, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE };
export default Pagination;
